import { supabase } from '../lib/supabase';

const settingsService = {
  /**
   * Get all settings for a specific category
   * @param {string} userId - User ID (required)
   * @param {string} category - Settings category (e.g., 'interface', 'security') (required)
   * @returns {Promise<Object>} Settings object with all keys
   */
  async getSettings(userId, category) {
    try {
      // Validate required parameters
      if (!userId) {
        throw new Error('userId is required to fetch settings');
      }
      
      if (!category) {
        throw new Error('category is required to fetch settings');
      }

      const { data, error } = await supabase
        ?.from('system_settings')
        ?.select('setting_key, setting_value')
        ?.eq('user_id', userId)
        ?.eq('setting_category', category);

      if (error) throw error;

      // Convert array of settings to object
      const settings = {};
      data?.forEach(item => {
        settings[item.setting_key] = item?.setting_value;
      });

      return settings;
    } catch (error) {
      console.error('Error fetching settings:', error);
      throw error;
    }
  },

  /**
   * Update or insert a specific setting
   * @param {string} userId - User ID
   * @param {string} category - Settings category
   * @param {string} key - Setting key
   * @param {Object} value - Setting value (JSONB)
   * @returns {Promise<Object>} Updated setting
   */
  async upsertSetting(userId, category, key, value) {
    try {
      const { data, error } = await supabase?.from('system_settings')?.upsert({
          user_id: userId,
          setting_category: category,
          setting_key: key,
          setting_value: value
        }, {
          onConflict: 'user_id,setting_category,setting_key'
        })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error upserting setting:', error);
      throw error;
    }
  },

  /**
   * Update multiple settings at once
   * @param {string} userId - User ID
   * @param {string} category - Settings category
   * @param {Object} settings - Object with key-value pairs
   * @returns {Promise<Array>} Array of updated settings
   */
  async upsertMultipleSettings(userId, category, settings) {
    try {
      const promises = Object.entries(settings)?.map(([key, value]) =>
        this.upsertSetting(userId, category, key, value)
      );

      const results = await Promise.all(promises);
      return results;
    } catch (error) {
      console.error('Error upserting multiple settings:', error);
      throw error;
    }
  },

  /**
   * Delete a specific setting
   * @param {string} userId - User ID
   * @param {string} category - Settings category
   * @param {string} key - Setting key
   * @returns {Promise<void>}
   */
  async deleteSetting(userId, category, key) {
    try {
      const { error } = await supabase?.from('system_settings')?.delete()?.eq('user_id', userId)?.eq('setting_category', category)?.eq('setting_key', key);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting setting:', error);
      throw error;
    }
  }
};

export default settingsService;